import { apiClient } from "@/lib/api/client";
import type {
  UserCreateBody,
  UserDto,
  UserSelfProfileUpdateBody,
  UserUpdateBody,
} from "@/lib/api/types/enterprise";

export type UsersListResponse = {
  items: UserDto[];
  total: number;
};

export async function listUsers(
  tenantId?: string | null,
  workspaceScope?: "all" | null,
  options?: {
    search?: string | null;
    role?: string | null;
    limit?: number;
    offset?: number;
  },
): Promise<UsersListResponse> {
  const params: Record<string, string> = {};
  if (tenantId) {
    params.tenant_id = tenantId;
  }
  if (workspaceScope === "all") {
    params.workspace_scope = "all";
  }
  if (options?.search?.trim()) {
    params.search = options.search.trim();
  }
  if (options?.role?.trim()) {
    params.role = options.role.trim();
  }
  if (typeof options?.limit === "number") {
    params.limit = String(options.limit);
  }
  if (typeof options?.offset === "number") {
    params.offset = String(options.offset);
  }
  const { data } = await apiClient.get<UsersListResponse>("/users", {
    params,
    __skipTenantHeader: workspaceScope === "all",
  });
  return data;
}

export async function listWorkspaceRoster(
  tenantId: string,
  options: { moduleKey?: string; action?: string } = {},
): Promise<UsersListResponse> {
  const params: Record<string, string> = {
    tenant_id: tenantId,
    module_key: options.moduleKey ?? "automations",
    action: options.action ?? "any",
  };
  const { data } = await apiClient.get<UsersListResponse>("/users/workspace-roster", { params });
  return data;
}

export async function getUser(userId: string): Promise<UserDto> {
  const { data } = await apiClient.get<UserDto>(`/users/${userId}`);
  return data;
}

export async function createUser(body: UserCreateBody, tenantId?: string | null): Promise<UserDto> {
  const params: Record<string, string> = {};
  if (tenantId) {
    params.tenant_id = tenantId;
  }
  const { data } = await apiClient.post<UserDto>("/users", body, { params });
  return data;
}

export async function updateUser(userId: string, body: UserUpdateBody): Promise<UserDto> {
  const { data } = await apiClient.patch<UserDto>(`/users/${userId}`, body);
  return data;
}

export async function resendInvitation(userId: string): Promise<UserDto> {
  const { data } = await apiClient.post<UserDto>(`/users/${userId}/resend-invitation`);
  return data;
}

export async function getMyProfile(): Promise<UserDto> {
  const { data } = await apiClient.get<UserDto>("/users/me");
  return data;
}

export async function updateMyProfile(body: UserSelfProfileUpdateBody): Promise<UserDto> {
  const { data } = await apiClient.patch<UserDto>("/users/me/profile", body);
  return data;
}

/** FastAPI returns 204 No Content. */
export async function deleteUser(userId: string): Promise<void> {
  await apiClient.delete(`/users/${userId}`);
}
